import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'antd';
import PropTypes from 'prop-types';
import Input from '../../../../components/elements/input/Input';
import { createContact, updateContact } from '../../api/contactsApi';

const emptyForm = {
  firstName: '',
  lastName: '',
  info: '',
  phone: '',
  email: '',
};

const ContactModal = ({ isOpen, onClose, onSaved, contact }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isEdit = Boolean(contact?._id);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      info: contact?.info || '',
      phone: contact?.phone || '',
      email: contact?.email || '',
    });
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
  }, [isOpen, contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOk = useCallback(async () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();

    if (!firstName || !lastName) {
      setError('Ime i prezime su obavezni.');
      return;
    }

    const payload = {
      firstName,
      lastName,
      info: form.info.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
    };

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const response = isEdit
      ? await updateContact(contact._id, payload)
      : await createContact(payload);

    if (!response.ok) {
      setError(response.message);
      setLoading(false);
      return;
    }

    setSuccessMessage(
      response.message || (isEdit ? 'Kontakt azuriran.' : 'Kontakt kreiran.')
    );
    setDone(true);
    onSaved?.();

    setTimeout(() => {
      setDone(false);
      onClose();
    }, 900);

    setLoading(false);
  }, [form, isEdit, contact?._id, onClose, onSaved]);

  const handleCancel = () => {
    setForm(emptyForm);
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
    onClose();
  };

  return (
    <Modal
      className="create_user_modal"
      title={isEdit ? 'Izmeni kontakt' : 'Dodaj kontakt'}
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '170px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : isEdit ? (
            'Izmeni kontakt'
          ) : (
            'Dodaj kontakt'
          )}
        </Button>,
      ]}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <Input
          required
          label="Ime"
          id="contactFirstName"
          name="firstName"
          placeholder="Ime"
          value={form.firstName}
          onChange={handleChange}
        />
        <Input
          required
          label="Prezime"
          id="contactLastName"
          name="lastName"
          placeholder="Prezime"
          value={form.lastName}
          onChange={handleChange}
        />
        <Input
          label="Informacije"
          id="contactInfo"
          name="info"
          placeholder="Informacije"
          value={form.info}
          onChange={handleChange}
        />
        <Input
          label="Broj telefona"
          id="contactPhone"
          name="phone"
          placeholder="Broj telefona"
          value={form.phone}
          onChange={handleChange}
        />
        <Input
          type="email"
          autoComplete="off"
          label="Email"
          id="contactEmail"
          name="email"
          placeholder="Email"
          value={form.email}
          onChange={handleChange}
        />
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

ContactModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  contact: PropTypes.shape({
    _id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    info: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default ContactModal;
